import React from "react";
import { useNavigate } from "react-router-dom";

const tools = [
  {
    id: "job-hunter",
    name: "Cloud Job Hunter",
    description:
      "AI-powered job scraping pipeline. Scores listings against your resume, generates tailored cover letters, and tracks applications through a Kanban board.",
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    path: "/job-hunter",
    color: "from-blue-500 to-indigo-600",
    status: "live",
  },
  {
    id: "cloud-cost",
    name: "Cloud Cost Tracker",
    description:
      "Monitor and optimize your AWS spending. Visualize cost breakdowns, set budgets, and get alerts when thresholds are exceeded.",
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    path: null,
    color: "from-emerald-500 to-teal-600",
    status: "coming",
  },
  {
    id: "infra-monitor",
    name: "Infra Health Monitor",
    description:
      "Real-time dashboard for your cloud infrastructure. Track uptime, latency, and resource utilization across services.",
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    path: null,
    color: "from-purple-500 to-violet-600",
    status: "coming",
  },
  {
    id: "cert-tracker",
    name: "Cert Study Tracker",
    description:
      "Track progress toward AWS, Azure, and GCP certifications. Organize study plans, practice exams, and revision schedules.",
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
      </svg>
    ),
    path: null,
    color: "from-amber-500 to-orange-600",
    status: "coming",
  },
];

export default function ToolsHub() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-950 to-black" />
        <div className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: "radial-gradient(circle at 25% 25%, rgba(59,130,246,0.15) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(139,92,246,0.1) 0%, transparent 50%)"
          }}
        />
        <div className="relative max-w-5xl mx-auto px-6 pt-16 pb-12 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-gray-400 mb-6">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            Private Tools
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight mb-3">
            ToolBox
          </h1>
          <p className="text-lg text-gray-400 max-w-xl mx-auto">
            Personal cloud engineering toolkit. Automation, monitoring, and career tools — all in one place.
          </p>
        </div>
      </div>

      {/* Tool Cards Grid */}
      <div className="max-w-5xl mx-auto px-6 pb-20 -mt-2">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {tools.map((tool) => (
            <div
              key={tool.id}
              className={`group relative bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden transition-all duration-300 ${
                tool.status === "live"
                  ? "hover:border-gray-600 hover:shadow-2xl hover:shadow-blue-500/5 cursor-pointer"
                  : "opacity-60"
              }`}
              onClick={() => tool.path && navigate(tool.path)}
            >
              {/* Gradient accent bar */}
              <div className={`h-1 bg-gradient-to-r ${tool.color}`} />

              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className={`p-2.5 rounded-xl bg-gradient-to-br ${tool.color} text-white`}>
                    {tool.icon}
                  </div>
                  {tool.status === "live" ? (
                    <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-green-500/10 text-green-400 border border-green-500/20">
                      Live
                    </span>
                  ) : (
                    <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-gray-800 text-gray-500 border border-gray-700">
                      Coming Soon
                    </span>
                  )}
                </div>

                <h3 className="text-lg font-semibold text-white mb-2">
                  {tool.name}
                </h3>
                <p className="text-sm text-gray-400 leading-relaxed">
                  {tool.description}
                </p>

                {tool.status === "live" && (
                  <div className="mt-5 flex items-center text-sm font-medium text-blue-400 group-hover:text-blue-300 transition-colors">
                    Open Tool
                    <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-12 text-center text-sm text-gray-600">
          Built with React + AWS Serverless
        </div>
      </div>
    </div>
  );
}
